import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Divider,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import DWLFLoader from '../../components/DWLFLoader';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  deleteDailyTrendlines,
  triggerTrendlineDetection,
  deleteWeeklyTrendlines,
  triggerWeeklyTrendlineDetection,
  backfillDailyCandles,
  backfillWeeklyCandles,
  backfillSwings,
  backfillHourlyData,
  backfillIndicatorEvents,
  clearEvents,
  clearCustomEvents,
  runCustomEventEvaluation,
  getSystemSettings,
  updateSystemSetting,
  deleteSystemSetting,
  getAdminSymbols,
  createAdminSymbol,
  updateAdminSymbol,
  getSymbolTags,
  addSymbolTag,
  removeSymbolTag,
  getAllTags,
  getAdminAccounts,
  activateAccount,
} from '../../api/admin';
import usePermissions from '../../hooks/usePermissions';
import DisclaimerAdmin from './DisclaimerAdmin';
import RoleManagement from './RoleManagement';
import UserRoleAssignment from './UserRoleAssignment';
import QuotaManagement from './QuotaManagement';
import Analytics from './Analytics';
import { isRegistrationOpen } from './registrationSetting';
import CycleSettingsSection from './cycles/CycleSettingsSection';

const SETTING_SELECT_OPTIONS = {
  'agent_model': [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fastest, Cheapest)' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (Fast, Smarter)' },
    { value: 'gpt-4o', label: 'GPT-4o (Balanced)' },
    { value: 'gpt-4.1', label: 'GPT-4.1 (Best Non-Reasoning)' },
    { value: 'gpt-5-mini', label: 'GPT-5 Mini (Agentic, Recommended)' },
    { value: 'o4-mini', label: 'o4-mini (Reasoning, Slower)' },
  ],
};

const SETTING_DESCRIPTIONS = {
  'agent_model': 'OpenAI model for the DWLF Agent. GPT-4o-mini is fastest/cheapest. GPT-5-mini recommended for best agentic capabilities. o4-mini enables reasoning but is slower.',
  'indicators.cycleLow.weekly.clusterRange': 'Maximum bar distance between neighbouring weekly swing lows that are grouped into a single cycle-low cluster.',
  'indicators.cycleLow.weekly.dssOversoldLevel': 'DSS oversold threshold used when scoring weekly cycle lows (default 20).',
  'indicators.cycleLow.weekly.dssOversoldLookback': 'Number of weekly bars before the pivot to scan for a DSS oversold touch.',
  'indicators.cycleLow.weekly.dssConfirmBars': 'Standard DSS cross-up confirmation window (in bars) after the pivot.',
  'indicators.cycleLow.weekly.dssExtendedConfirmBars': 'Extended DSS cross-up window used when DSS is choppy in the 15–30 band around the pivot.',
  'indicators.cycleLow.weekly.weeklySmaReclaimBars': 'Maximum bars after the pivot for price to reclaim the weekly 10-SMA.',
  'indicators.cycleLow.weekly.weeklySmaHoldBars': 'Number of consecutive bars required to hold above the weekly 10-SMA after reclaim.',
  'indicators.cycleLow.weekly.acceptThreshold': 'Score threshold required for a weekly cycle low to be accepted (batch and live modes).',
  'indicators.cycleHigh.weekly.clusterRange': 'Maximum bar distance between neighbouring weekly swing highs that are grouped into a single cycle-high cluster.',
  'indicators.cycleHigh.weekly.dssOverboughtLevel': 'DSS overbought threshold used when scoring weekly cycle highs (default 80).',
  'indicators.cycleHigh.weekly.dssOverboughtLookback': 'Number of weekly bars before the pivot to scan for a DSS overbought touch.',
  'indicators.cycleHigh.weekly.dssConfirmBars': 'Standard DSS cross-down confirmation window (in bars) after the pivot.',
  'indicators.cycleHigh.weekly.dssExtendedConfirmBars': 'Extended DSS cross-down window used when DSS is choppy in the 60–80 band around the pivot.',
  'indicators.cycleHigh.weekly.weeklySmaRejectionBars': 'Maximum bars after the pivot for price to reject the weekly 10-SMA from below.',
  'indicators.cycleHigh.weekly.weeklySmaHoldBelowBars': 'Number of consecutive bars required to hold below the weekly 10-SMA after rejection.',
  'indicators.cycleHigh.weekly.acceptThreshold': 'Score threshold required for a weekly cycle high to be accepted (batch and live modes).',
  'indicators.swing.sweepBars': 'Maximum number of candles allowed to reclaim a swing sweep event.',
  'indicators.weeklyCycle.sweepBars': 'Maximum number of weekly bars allowed to reclaim a cycle sweep event.',
  'indicators.emaCloud.lengths': 'Comma-separated EMA lengths that form the cloud (minimum two, positive integers).',
  'indicators.emaCloud.alignmentConfirmation': 'Number of consecutive candles required before emitting sustained alignment events.',
};

const INDICATOR_SETTING_KEYS = Object.freeze({
  dss: {
    length: 'indicators.dss.length',
    smooth1: 'indicators.dss.smooth1',
    signal: 'indicators.dss.signal',
  },
  bollinger: {
    length: 'indicators.bollinger.length',
    basis: 'indicators.bollinger.basis',
    source: 'indicators.bollinger.source',
    standardDeviation: 'indicators.bollinger.standardDeviation',
    offset: 'indicators.bollinger.offset',
  },
  swing: {
    daily: 'indicators.swing.daily.lookback',
    weekly: 'indicators.swing.weekly.lookback',
    hourly: 'indicators.swing.hourly.lookback',
    sweepBars: 'indicators.swing.sweepBars',
  },
  weeklyCycleSweep: {
    sweepBars: 'indicators.weeklyCycle.sweepBars',
  },
  // Cycle low/high settings now live in the per-TF CycleSettingsSection
  // component (six instances under the Indicator Settings tab). The keys
  // are produced by the library's cycleLow/HighSettingKey helper and
  // don't need to be enumerated here.
  emaCloud: {
    lengths: 'indicators.emaCloud.lengths',
    alignmentConfirmation: 'indicators.emaCloud.alignmentConfirmation',
  },
});

const DEFAULT_DSS_PARAMS = Object.freeze({
  length: 13,
  smooth1: 8,
  signal: 3,
});

const DEFAULT_BOLLINGER_PARAMS = Object.freeze({
  length: 20,
  basis: 'sma',
  source: 'close',
  standardDeviation: 2,
  offset: 0,
});

const DEFAULT_SWING_PARAMS = Object.freeze({
  daily: 5,
  weekly: 3,
  hourly: 5,
  sweepBars: 1,
});

const DEFAULT_WEEKLY_CYCLE_SWEEP_PARAMS = Object.freeze({
  sweepBars: 1,
});

const DEFAULT_EMA_CLOUD_PARAMS = Object.freeze({
  lengths: [10, 20, 50, 200],
  alignmentConfirmation: 10,
});

const DSS_FIELDS = ['length', 'smooth1', 'signal'];

const BOLLINGER_FIELDS = ['length', 'basis', 'source', 'standardDeviation', 'offset'];

const SWING_FIELDS = ['daily', 'weekly', 'hourly', 'sweepBars'];
const WEEKLY_CYCLE_SWEEP_FIELDS = ['sweepBars'];

const DSS_FIELD_CONFIG = [
  {
    field: 'length',
    label: 'Lookback Length',
    helperText: 'Number of candles used for the stochastic range before smoothing.',
  },
  {
    field: 'smooth1',
    label: 'Primary Smoothing',
    helperText: 'EMA length applied in the first smoothing pass.',
  },
  {
    field: 'signal',
    label: 'Signal Line',
    helperText: 'EMA length used for the DSS signal line.',
  },
];

const BOLLINGER_FIELD_CONFIG = [
  {
    field: 'length',
    label: 'Rolling Length',
    helperText: 'Number of candles used to compute the basis average and deviation.',
    type: 'number',
    inputProps: { min: 1, step: 1 },
  },
  {
    field: 'basis',
    label: 'Basis Type',
    helperText: 'Moving-average type used for the middle band.',
    type: 'select',
    options: [
      { value: 'sma', label: 'Simple (SMA)' },
      { value: 'ema', label: 'Exponential (EMA)' },
    ],
  },
  {
    field: 'source',
    label: 'Source Price',
    helperText: 'Candle price applied when deriving the bands.',
    type: 'select',
    options: [
      { value: 'open', label: 'Open' },
      { value: 'high', label: 'High' },
      { value: 'low', label: 'Low' },
      { value: 'close', label: 'Close' },
    ],
  },
  {
    field: 'standardDeviation',
    label: 'Standard Deviations',
    helperText: 'Multiplier applied to the rolling standard deviation.',
    type: 'number',
    inputProps: { min: 0, step: 0.1 },
  },
  {
    field: 'offset',
    label: 'Offset',
    helperText: 'Number of bars to shift the bands forward (positive) or backward (negative).',
    type: 'number',
    inputProps: { step: 1 },
  },
];

const SWING_FIELD_CONFIG = [
  {
    field: 'daily',
    label: 'Daily Lookback',
    helperText: 'Candles on each side required to confirm a daily swing high/low.',
  },
  {
    field: 'weekly',
    label: 'Weekly Lookback',
    helperText: 'Candles on each side required to confirm a weekly swing high/low.',
  },
  {
    field: 'hourly',
    label: 'Hourly Lookback',
    helperText: 'Candles on each side required to confirm an hourly swing high/low.',
  },
  {
    field: 'sweepBars',
    label: 'Sweep Bars',
    helperText: 'Maximum candles allowed to reclaim a swing sweep event.',
  },
];

const WEEKLY_CYCLE_SWEEP_FIELD_CONFIG = [
  {
    field: 'sweepBars',
    label: 'Cycle Sweep Bars',
    helperText: SETTING_DESCRIPTIONS['indicators.weeklyCycle.sweepBars'],
    type: 'number',
    inputProps: { min: 1, step: 1 },
  },
];

const EMA_CLOUD_FIELDS = ['lengths', 'alignmentConfirmation'];

const EMA_CLOUD_FIELD_CONFIG = [
  {
    field: 'lengths',
    label: 'EMA Lengths',
    helperText: SETTING_DESCRIPTIONS['indicators.emaCloud.lengths'],
  },
  {
    field: 'alignmentConfirmation',
    label: 'Sustained Alignment Candles',
    helperText: SETTING_DESCRIPTIONS['indicators.emaCloud.alignmentConfirmation'],
    type: 'number',
    inputProps: { min: 1, step: 1 },
  },
];

const toFormValue = (value, fallback) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '') {
      return trimmed;
    }
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return String(fallback);
};

const createDefaultDssForm = () => ({
  length: String(DEFAULT_DSS_PARAMS.length),
  smooth1: String(DEFAULT_DSS_PARAMS.smooth1),
  signal: String(DEFAULT_DSS_PARAMS.signal),
});

const createDefaultBollingerForm = () => ({
  length: String(DEFAULT_BOLLINGER_PARAMS.length),
  basis: DEFAULT_BOLLINGER_PARAMS.basis,
  source: DEFAULT_BOLLINGER_PARAMS.source,
  standardDeviation: String(DEFAULT_BOLLINGER_PARAMS.standardDeviation),
  offset: String(DEFAULT_BOLLINGER_PARAMS.offset),
});

const createDefaultSwingForm = () => ({
  daily: String(DEFAULT_SWING_PARAMS.daily),
  weekly: String(DEFAULT_SWING_PARAMS.weekly),
  hourly: String(DEFAULT_SWING_PARAMS.hourly),
  sweepBars: String(DEFAULT_SWING_PARAMS.sweepBars),
});

const createDefaultWeeklyCycleSweepForm = () =>
  WEEKLY_CYCLE_SWEEP_FIELDS.reduce((acc, field) => {
    const fallback = DEFAULT_WEEKLY_CYCLE_SWEEP_PARAMS[field];
    acc[field] = toFormValue(fallback, fallback);
    return acc;
  }, {});

const createDefaultEmaCloudForm = () =>
  EMA_CLOUD_FIELDS.reduce((acc, field) => {
    const fallback = DEFAULT_EMA_CLOUD_PARAMS[field];
    const effectiveFallback = Array.isArray(fallback) ? fallback.join(',') : fallback;
    acc[field] = toFormValue(effectiveFallback, effectiveFallback);
    return acc;
  }, {});

const DEFAULT_SUPPORTED_TIMEFRAMES = Object.freeze({
  weekly: true,
  daily: true,
  '4h': false,
  '1h': false,
});

const INDICATOR_BACKFILL_YEARS_BUDGET = 40;

const normalizeBooleanFlag = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const normalizeSupportedTimeframes = (input = {}) => {
  const normalized = { ...DEFAULT_SUPPORTED_TIMEFRAMES };

  if (Array.isArray(input)) {
    input.forEach((timeframe) => {
      if (timeframe === '4h' || timeframe === '1h') {
        normalized[timeframe] = true;
      }
    });
    return normalized;
  }

  Object.entries(input || {}).forEach(([key, value]) => {
    if (!Object.prototype.hasOwnProperty.call(normalized, key)) {
      return;
    }

    if (key === 'weekly' || key === 'daily') {
      normalized[key] = true;
      return;
    }

    normalized[key] = normalizeBooleanFlag(value);
  });

  return normalized;
};

const normalizeAdminSymbolRecord = (item = {}) => ({
  symbol: item.symbol,
  name: item.name || '',
  isActive: normalizeBooleanFlag(item.isActive),
  supportedTimeframes: normalizeSupportedTimeframes(item.supportedTimeframes),
});

const createDefaultSupportedTimeframes = () => ({
  '4h': DEFAULT_SUPPORTED_TIMEFRAMES['4h'],
  '1h': DEFAULT_SUPPORTED_TIMEFRAMES['1h'],
});

const createDefaultNewSymbolForm = () => ({
  symbol: '',
  name: '',
  isActive: true,
  supportedTimeframes: createDefaultSupportedTimeframes(),
});

const AdminPage = () => {
  const user = useSelector((state) => state.account.user);
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [detectLoading, setDetectLoading] = useState(false);
  const [weeklySymbol, setWeeklySymbol] = useState('');
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyDetectLoading, setWeeklyDetectLoading] = useState(false);
  const [backfillSymbol, setBackfillSymbol] = useState('');
  const [backfillYears, setBackfillYears] = useState('3');
  const [backfillHours, setBackfillHours] = useState('500');
  const [indicatorEventsYears, setIndicatorEventsYears] = useState('3');
  const [eventSymbolsInput, setEventSymbolsInput] = useState('');
  const [customEventSymbolsInput, setCustomEventSymbolsInput] = useState('');
  const [customEventAccountOnly, setCustomEventAccountOnly] = useState(true);
  const [customEventLookbackDays, setCustomEventLookbackDays] = useState('');
  const [customEventClearSymbolsInput, setCustomEventClearSymbolsInput] = useState('');
  const [customEventClearAccountOnly, setCustomEventClearAccountOnly] = useState(true);
  const [backfillLoading, setBackfillLoading] = useState({
    dailyCandles: false,
    weeklyCandles: false,
    swingsDaily: false,
    swingsWeekly: false,
    swingsBoth: false,
    hourly: false,
    indicatorEvents: false,
    customEvents: false,
    customEventClear: false,
    clearEvents: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settings, setSettings] = useState([]);
  const [registrationEnabled, setRegistrationEnabled] = useState(null); // null = loading
  const [registrationToggling, setRegistrationToggling] = useState(false);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState('');
  const [symbols, setSymbols] = useState([]);
  const [symbolTags, setSymbolTags] = useState({});
  const [allTags, setAllTags] = useState([]);
  const [updatingSymbols, setUpdatingSymbols] = useState({});
  const [addingSymbol, setAddingSymbol] = useState(false);
  const [newSymbolForm, setNewSymbolForm] = useState(() => createDefaultNewSymbolForm());
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [users, setUsers] = useState([]);
  const [activatingAccountId, setActivatingAccountId] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');
  const [dssForm, setDssForm] = useState(() => createDefaultDssForm());
  const [dssInitialForm, setDssInitialForm] = useState(() => createDefaultDssForm());
  const [dssFieldErrors, setDssFieldErrors] = useState({});
  const [bollingerForm, setBollingerForm] = useState(() => createDefaultBollingerForm());
  const [bollingerInitialForm, setBollingerInitialForm] = useState(() => createDefaultBollingerForm());
  const [bollingerFieldErrors, setBollingerFieldErrors] = useState({});
  const [emaCloudForm, setEmaCloudForm] = useState(() => createDefaultEmaCloudForm());
  const [emaCloudInitialForm, setEmaCloudInitialForm] = useState(() => createDefaultEmaCloudForm());
  const [emaCloudFieldErrors, setEmaCloudFieldErrors] = useState({});
  const [swingForm, setSwingForm] = useState(() => createDefaultSwingForm());
  const [swingInitialForm, setSwingInitialForm] = useState(() => createDefaultSwingForm());
  const [swingFieldErrors, setSwingFieldErrors] = useState({});
  const [weeklyCycleSweepForm, setWeeklyCycleSweepForm] = useState(() =>
    createDefaultWeeklyCycleSweepForm(),
  );
  const [weeklyCycleSweepInitialForm, setWeeklyCycleSweepInitialForm] = useState(() =>
    createDefaultWeeklyCycleSweepForm(),
  );
  const [weeklyCycleSweepFieldErrors, setWeeklyCycleSweepFieldErrors] = useState({});
  // Cycle low/high state (form, scope, validation, save) lives in
  // <CycleSettingsSection>; instances are rendered six-times below
  // (low/high × weekly/daily/hourly).
  const [indicatorError, setIndicatorError] = useState('');
  const [dssSaving, setDssSaving] = useState(false);
  const [bollingerSaving, setBollingerSaving] = useState(false);
  const [emaCloudSaving, setEmaCloudSaving] = useState(false);
  const [swingSaving, setSwingSaving] = useState(false);
  const [weeklyCycleSweepSaving, setWeeklyCycleSweepSaving] = useState(false);
  // Cycle low/high saving state lives in each <CycleSettingsSection>
  // instance — they don't contribute to indicatorBusy because each section
  // already self-disables via its own local saving flag and edits don't
  // overlap with other indicators.
  const indicatorBusy =
    dssSaving ||
    bollingerSaving ||
    swingSaving ||
    weeklyCycleSweepSaving ||
    emaCloudSaving;
  const transformSymbols = useCallback(
    (rawSymbols = []) =>
      rawSymbols
        .map((item) => normalizeAdminSymbolRecord(item))
        .filter((item) => item && item.symbol)
        .sort((a, b) => a.symbol.localeCompare(b.symbol)),
    []
  );
  const { isAdmin, hasPermission, loading: permissionsLoading, loaded: permissionsLoaded } = usePermissions();
  const userIsAdmin = isAdmin();
  // Admins can manage roles (admin:* grants full access, or explicit roles:write)
  const canManageRoles = userIsAdmin || hasPermission('roles:write');
  // Admins can manage quotas (admin:* grants full access, or explicit quotas:write)
  const canManageQuotas = userIsAdmin || hasPermission('quotas:write');

  // Compute valid tab to prevent MUI warning when activeTab doesn't match available tabs
  // Non-admin users can only access tabs they have permissions for
  const validTab = useMemo(() => {
    const adminOnlyTabs = ['analytics', 'symbols', 'trendlines', 'indicators', 'settings', 'disclaimer', 'users'];
    if (!userIsAdmin && adminOnlyTabs.includes(activeTab)) {
      // Redirect to the first available tab the user has access to
      if (canManageRoles) {return 'roles';}
      if (canManageQuotas) {return 'quotas';}
      return 'roles'; // fallback
    }
    return activeTab;
  }, [activeTab, userIsAdmin, canManageRoles, canManageQuotas]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const dssDirty = useMemo(
    () => DSS_FIELDS.some((field) => dssForm[field] !== dssInitialForm[field]),
    [dssForm, dssInitialForm]
  );

  const bollingerDirty = useMemo(
    () => BOLLINGER_FIELDS.some((field) => bollingerForm[field] !== bollingerInitialForm[field]),
    [bollingerForm, bollingerInitialForm]
  );

  const emaCloudDirty = useMemo(
    () => EMA_CLOUD_FIELDS.some((field) => emaCloudForm[field] !== emaCloudInitialForm[field]),
    [emaCloudForm, emaCloudInitialForm]
  );

  const swingDirty = useMemo(
    () => SWING_FIELDS.some((field) => swingForm[field] !== swingInitialForm[field]),
    [swingForm, swingInitialForm]
  );

  const weeklyCycleSweepDirty = useMemo(
    () =>
      WEEKLY_CYCLE_SWEEP_FIELDS.some(
        (field) => weeklyCycleSweepForm[field] !== weeklyCycleSweepInitialForm[field],
      ),
    [weeklyCycleSweepForm, weeklyCycleSweepInitialForm],
  );

  const serializeSettingValue = (value) => {
    if (value === null || value === undefined) { return ''; }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const parseSettingValue = (input) => {
    const trimmed = input.trim();
    if (trimmed === '') {
      return '';
    }
    if (trimmed.toLowerCase() === 'true') {
      return true;
    }
    if (trimmed.toLowerCase() === 'false') {
      return false;
    }
    if (!Number.isNaN(Number(trimmed)) && trimmed !== '') {
      return Number(trimmed);
    }
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        // fall through to returning the raw string
      }
    }
    return trimmed;
  };

  const loadSettings = useCallback(async () => {
    if (!userIsAdmin) {
      return;
    }

    try {
      setSettingsLoading(true);
      setSettingsError('');
      const response = await getSystemSettings({});
      const entries = Object.entries(response?.settings || {});
      const mapped = entries
        .map(([key, meta]) => ({
          key,
          value: meta?.value,
          isDefault: !!meta?.isDefault,
          updatedAt: meta?.updatedAt || null,
          inputValue: serializeSettingValue(meta?.value),
          dirty: false,
          saving: false,
          options: SETTING_SELECT_OPTIONS[key] || null,
        }))
        .sort((a, b) => a.key.localeCompare(b.key));
      setSettings(mapped);

      // Extract registration.enabled from the loaded settings
      const regSetting = mapped.find((s) => s.key === 'registration.enabled');
      setRegistrationEnabled(isRegistrationOpen(regSetting?.value));
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load system settings.';
      setSettingsError(message);
      setSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  }, [userIsAdmin]);

  const loadSymbols = useCallback(async () => {
    if (!userIsAdmin) {
      return;
    }

    setSymbolsLoading(true);
    setSymbolsError('');
    let loadedSymbols = [];
    try {
      const response = await getAdminSymbols();
      loadedSymbols = transformSymbols(response?.symbols || []);
      setSymbols(loadedSymbols);
      setSymbolsLoading(false);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load symbols.';
      setSymbolsError(message);
      setSymbols([]);
      setSymbolsLoading(false);
      return;
    }

    try {
      const normalizeTag = (tag) => (typeof tag === 'string' ? tag.toLowerCase() : '');
      const normalizeAndDedupeTags = (tags) => [...new Set((tags || []).map(normalizeTag).filter(Boolean))];
      setSymbolTags(
        loadedSymbols.reduce((acc, symbol) => {
          acc[symbol.symbol] = [];
          return acc;
        }, {})
      );

      const tagsResponse = await getAllTags().catch(() => ({ tags: [] }));
      setAllTags(normalizeAndDedupeTags(tagsResponse?.tags));

      loadedSymbols.forEach((symbol) => {
        getSymbolTags(symbol.symbol)
          .then((response) => {
            setSymbolTags((prev) => ({
              ...prev,
              [symbol.symbol]: normalizeAndDedupeTags(response?.tags),
            }));
          })
          .catch(() => {
            setSymbolTags((prev) => ({
              ...prev,
              [symbol.symbol]: [],
            }));
          });
      });
    } catch (error) {
      setAllTags([]);
      setSymbolTags({});
    }
  }, [userIsAdmin, transformSymbols]);

  const loadUsers = useCallback(async () => {
    if (!userIsAdmin) {
      return;
    }

    try {
      setUsersLoading(true);
      setUsersError('');
      const response = await getAdminAccounts();
      const raw = response?.accounts || response?.data?.accounts || [];
      setUsers(raw.map(u => ({ ...u, status: u.status || u.activationStatus || (u.verified ? 'active' : 'unknown') })));
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load accounts.';
      setUsersError(message);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [userIsAdmin]);

  const handleActivateAccount = async (accountId) => {
    if (activatingAccountId) {
      return;
    }

    try {
      setActivatingAccountId(accountId);
      await activateAccount(accountId);
      await loadUsers();
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Account activated successfully.',
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to activate account.';
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
      return;
    } finally {
      setActivatingAccountId(null);
    }
  };

  useEffect(() => {
    if (userIsAdmin) {
      loadSettings();
    }
  }, [userIsAdmin, loadSettings]);

  useEffect(() => {
    if (userIsAdmin) {
      loadSymbols();
    }
  }, [userIsAdmin, loadSymbols]);

  useEffect(() => {
    if (userIsAdmin) {
      loadUsers();
    }
  }, [userIsAdmin, loadUsers]);

  useEffect(() => {
    const nextDssFormValues = DSS_FIELDS.reduce((accumulator, field) => {
      const key = INDICATOR_SETTING_KEYS.dss[field];
      const fallback = DEFAULT_DSS_PARAMS[field];
      const setting = settings.find((item) => item.key === key);
      accumulator[field] = toFormValue(setting?.value, fallback);
      return accumulator;
    }, {});

    const nextBollingerFormValues = BOLLINGER_FIELDS.reduce((accumulator, field) => {
      const key = INDICATOR_SETTING_KEYS.bollinger[field];
      const fallback = DEFAULT_BOLLINGER_PARAMS[field];
      const setting = settings.find((item) => item.key === key);
      accumulator[field] = toFormValue(setting?.value, fallback);
      return accumulator;
    }, {});

    const nextEmaCloudFormValues = EMA_CLOUD_FIELDS.reduce((accumulator, field) => {
      const key = INDICATOR_SETTING_KEYS.emaCloud[field];
      const fallback = DEFAULT_EMA_CLOUD_PARAMS[field];
      const effectiveFallback = Array.isArray(fallback) ? fallback.join(',') : fallback;
      const setting = settings.find((item) => item.key === key);
      const rawValue = Array.isArray(setting?.value) ? setting.value.join(',') : setting?.value;
      accumulator[field] = toFormValue(rawValue, effectiveFallback);
      return accumulator;
    }, {});

    const nextSwingFormValues = SWING_FIELDS.reduce((accumulator, field) => {
      const key = INDICATOR_SETTING_KEYS.swing[field];
      const fallback = DEFAULT_SWING_PARAMS[field];
      const setting = settings.find((item) => item.key === key);
      accumulator[field] = toFormValue(setting?.value, fallback);
      return accumulator;
    }, {});

    const nextWeeklyCycleSweepFormValues = WEEKLY_CYCLE_SWEEP_FIELDS.reduce((accumulator, field) => {
      const key = INDICATOR_SETTING_KEYS.weeklyCycleSweep[field];
      const fallback = DEFAULT_WEEKLY_CYCLE_SWEEP_PARAMS[field];
      const setting = settings.find((item) => item.key === key);
      accumulator[field] = toFormValue(setting?.value, fallback);
      return accumulator;
    }, {});

    setDssForm(nextDssFormValues);
    setDssInitialForm({ ...nextDssFormValues });
    setDssFieldErrors({});

    setBollingerForm(nextBollingerFormValues);
    setBollingerInitialForm({ ...nextBollingerFormValues });
    setBollingerFieldErrors({});

    setEmaCloudForm(nextEmaCloudFormValues);
    setEmaCloudInitialForm({ ...nextEmaCloudFormValues });
    setEmaCloudFieldErrors({});

    setSwingForm(nextSwingFormValues);
    setSwingInitialForm({ ...nextSwingFormValues });
    setSwingFieldErrors({});

    setWeeklyCycleSweepForm(nextWeeklyCycleSweepFormValues);
    setWeeklyCycleSweepInitialForm({ ...nextWeeklyCycleSweepFormValues });
    setWeeklyCycleSweepFieldErrors({});

    setIndicatorError('');
  }, [settings]);

  const formatUpdatedAt = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const updateSettingState = (settingKey, updater) => {
    setSettings((prev) =>
      prev.map((setting) =>
        setting.key === settingKey ? { ...setting, ...updater(setting) } : setting
      )
    );
  };

  const handleSettingInputChange = (settingKey, newValue) => {
    updateSettingState(settingKey, (setting) => ({
      inputValue: newValue,
      dirty: newValue !== serializeSettingValue(setting.value),
    }));
  };

  const handleSaveSetting = async (settingKey) => {
    const current = settings.find((setting) => setting.key === settingKey);
    if (!current || current.saving) {
      return;
    }

    const parsedValue = parseSettingValue(current.inputValue);

    updateSettingState(settingKey, () => ({ saving: true }));

    try {
      await updateSystemSetting(settingKey, parsedValue);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Setting "${settingKey}" updated successfully.`,
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to update setting "${settingKey}".`;
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
      updateSettingState(settingKey, () => ({ saving: false }));
    }
  };

  const handleResetSetting = async (settingKey) => {
    const current = settings.find((setting) => setting.key === settingKey);
    if (!current || current.saving || current.isDefault) {
      return;
    }

    updateSettingState(settingKey, () => ({ saving: true }));

    try {
      await deleteSystemSetting(settingKey);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Setting "${settingKey}" reset to default.`,
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to reset setting "${settingKey}".`;
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
      updateSettingState(settingKey, () => ({ saving: false }));
    }
  };

  const handleToggleRegistration = async () => {
    const newValue = !registrationEnabled;
    setRegistrationToggling(true);
    try {
      await updateSystemSetting('registration.enabled', newValue);
      // Refresh the settings table — this also re-extracts
      // registrationEnabled from the server response, so no need
      // for an optimistic local state update.
      await loadSettings();
      setSnackbar({
        open: true,
        severity: 'success',
        message: newValue ? 'Registration opened' : 'Registration closed',
      });
    } catch (error) {
      console.error('Registration toggle failed:', error?.response?.status, error?.response?.data || error?.message);
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Failed to ${newValue ? 'open' : 'close'} registration: ${error?.response?.data?.error || error?.message || 'unknown error'}`,
      });
    } finally {
      setRegistrationToggling(false);
    }
  };

  const handleRefreshSettings = () => {
    loadSettings();
  };

  const handleRefreshSymbols = () => {
    loadSymbols();
  };

  const handleRefreshUsers = () => {
    loadUsers();
  };

  const handleDssInputChange = (field) => (event) => {
    const { value } = event.target;
    setDssForm((prev) => ({ ...prev, [field]: value }));
    setDssFieldErrors((prevErrors) => {
      if (!prevErrors[field]) {
        return prevErrors;
      }
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setIndicatorError('');
  };

  const validateDssForm = useCallback((form) => {
    const errors = {};
    DSS_FIELDS.forEach((field) => {
      const raw = form[field];
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
        errors[field] = 'Must be a positive integer.';
      }
    });
    return errors;
  }, []);

  const handleSaveDss = async () => {
    const errors = validateDssForm(dssForm);
    if (Object.keys(errors).length > 0) {
      setDssFieldErrors(errors);
      setIndicatorError('Please fix the highlighted fields before saving.');
      return;
    }

    setDssSaving(true);
    setIndicatorError('');

    try {
      await Promise.all(
        DSS_FIELDS.map((field) =>
          updateSystemSetting(INDICATOR_SETTING_KEYS.dss[field], Number(dssForm[field]))
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'DSS indicator settings updated successfully.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update DSS indicator settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setDssSaving(false);
    }
  };

  const handleResetDss = async () => {
    setDssSaving(true);
    setIndicatorError('');
    setDssFieldErrors({});

    try {
      await Promise.all(
        DSS_FIELDS.map((field) =>
          deleteSystemSetting(INDICATOR_SETTING_KEYS.dss[field]).catch((error) => {
            if (error?.response?.status === 404) {
              return null;
            }
            throw error;
          })
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'DSS indicator settings reset to defaults.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to reset DSS indicator settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setDssSaving(false);
    }
  };

  const handleRevertDssChanges = () => {
    setDssForm({ ...dssInitialForm });
    setDssFieldErrors({});
    setIndicatorError('');
  };

  const handleBollingerInputChange = (field) => (event) => {
    const { value } = event.target;
    setBollingerForm((prev) => ({ ...prev, [field]: value }));
    setBollingerFieldErrors((prevErrors) => {
      if (!prevErrors[field]) {
        return prevErrors;
      }
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setIndicatorError('');
  };

  const handleEmaCloudInputChange = (field) => (event) => {
    const { value } = event.target;
    setEmaCloudForm((prev) => ({ ...prev, [field]: value }));
    setEmaCloudFieldErrors((prevErrors) => {
      if (!prevErrors[field]) {
        return prevErrors;
      }
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setIndicatorError('');
  };

  const validateBollingerForm = useCallback((form) => {
    const errors = {};

    const lengthRaw = form.length;
    const lengthValue = Number(lengthRaw);
    if (
      lengthRaw === '' ||
      !Number.isFinite(lengthValue) ||
      lengthValue <= 0 ||
      !Number.isInteger(lengthValue)
    ) {
      errors.length = 'Must be a positive integer.';
    }

    const deviationRaw = form.standardDeviation;
    const deviationValue = Number(deviationRaw);
    if (deviationRaw === '' || !Number.isFinite(deviationValue) || deviationValue < 0) {
      errors.standardDeviation = 'Must be a non-negative number.';
    }

    const offsetRaw = form.offset;
    const offsetValue = Number(offsetRaw);
    if (offsetRaw === '' || !Number.isFinite(offsetValue) || !Number.isInteger(offsetValue)) {
      errors.offset = 'Must be an integer.';
    }

    if (!['sma', 'ema'].includes(form.basis)) {
      errors.basis = 'Select SMA or EMA.';
    }

    if (!['open', 'high', 'low', 'close'].includes(form.source)) {
      errors.source = 'Select a valid price source.';
    }

    return errors;
  }, []);

  const parseEmaCloudLengths = (value) =>
    value
      .split(',')
      .map((entry) => Number(entry.trim()))
      .filter((num) => Number.isFinite(num));

  const validateEmaCloudForm = useCallback((form) => {
    const errors = {};
    const lengths = parseEmaCloudLengths(form.lengths);
    if (lengths.length < 2) {
      errors.lengths = 'Provide at least two EMA lengths.';
    } else if (lengths.some((num) => !Number.isInteger(num) || num <= 0)) {
      errors.lengths = 'Lengths must be positive integers.';
    }

    const confirmation = Number(form.alignmentConfirmation);
    if (!Number.isInteger(confirmation) || confirmation <= 0) {
      errors.alignmentConfirmation = 'Must be a positive integer.';
    }

    return { errors, lengths, confirmation };
  }, []);

  const handleSaveBollinger = async () => {
    const errors = validateBollingerForm(bollingerForm);
    if (Object.keys(errors).length > 0) {
      setBollingerFieldErrors(errors);
      setIndicatorError('Please fix the highlighted Bollinger Band fields before saving.');
      return;
    }

    setBollingerSaving(true);
    setIndicatorError('');

    try {
      await Promise.all(
        BOLLINGER_FIELDS.map((field) => {
          const key = INDICATOR_SETTING_KEYS.bollinger[field];
          if (!key) {
            return Promise.resolve();
          }
          if (field === 'basis' || field === 'source') {
            return updateSystemSetting(key, bollingerForm[field].trim());
          }
          return updateSystemSetting(key, Number(bollingerForm[field]));
        })
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Bollinger Band indicator settings updated successfully.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update Bollinger Band indicator settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setBollingerSaving(false);
    }
  };

  const handleResetBollinger = async () => {
    setBollingerSaving(true);
    setIndicatorError('');
    setBollingerFieldErrors({});

    try {
      await Promise.all(
        BOLLINGER_FIELDS.map((field) =>
          deleteSystemSetting(INDICATOR_SETTING_KEYS.bollinger[field]).catch((error) => {
            if (error?.response?.status === 404) {
              return null;
            }
            throw error;
          })
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Bollinger Band indicator settings reset to defaults.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to reset Bollinger Band indicator settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setBollingerSaving(false);
    }
  };

  const handleRevertBollingerChanges = () => {
    setBollingerForm({ ...bollingerInitialForm });
    setBollingerFieldErrors({});
    setIndicatorError('');
  };

  const handleSaveEmaCloud = async () => {
    const { errors, lengths, confirmation } = validateEmaCloudForm(emaCloudForm);
    if (Object.keys(errors).length > 0) {
      setEmaCloudFieldErrors(errors);
      setIndicatorError('Please fix the highlighted EMA Cloud fields before saving.');
      return;
    }

    setEmaCloudSaving(true);
    setIndicatorError('');

    try {
      await Promise.all([
        updateSystemSetting(INDICATOR_SETTING_KEYS.emaCloud.lengths, lengths),
        updateSystemSetting(INDICATOR_SETTING_KEYS.emaCloud.alignmentConfirmation, confirmation),
      ]);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'EMA Cloud settings updated successfully.',
      });
      setEmaCloudInitialForm({ ...emaCloudForm });
      await loadSettings();
    } catch (error) {
      console.error('AdminPage: Failed to update EMA Cloud settings', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to update EMA Cloud settings.',
      });
    } finally {
      setEmaCloudSaving(false);
    }
  };

  const handleResetEmaCloud = async () => {
    setEmaCloudSaving(true);
    setIndicatorError('');
    setEmaCloudFieldErrors({});

    try {
      await Promise.all([
        deleteSystemSetting(INDICATOR_SETTING_KEYS.emaCloud.lengths).catch((error) => {
          if (error?.response?.status === 404) {
            return null;
          }
          throw error;
        }),
        deleteSystemSetting(INDICATOR_SETTING_KEYS.emaCloud.alignmentConfirmation).catch((error) => {
          if (error?.response?.status === 404) {
            return null;
          }
          throw error;
        }),
      ]);
      const defaults = createDefaultEmaCloudForm();
      setEmaCloudForm(defaults);
      setEmaCloudInitialForm(defaults);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'EMA Cloud settings reset to defaults.',
      });
      await loadSettings();
    } catch (error) {
      console.error('AdminPage: Failed to reset EMA Cloud settings', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to reset EMA Cloud settings.',
      });
    } finally {
      setEmaCloudSaving(false);
    }
  };

  const handleRevertEmaCloudChanges = () => {
    setEmaCloudForm({ ...emaCloudInitialForm });
    setEmaCloudFieldErrors({});
    setIndicatorError('');
  };

  const handleSwingInputChange = (field) => (event) => {
    const { value } = event.target;
    setSwingForm((prev) => ({ ...prev, [field]: value }));
    setSwingFieldErrors((prevErrors) => {
      if (!prevErrors[field]) {
        return prevErrors;
      }
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setIndicatorError('');
  };

  const validateSwingForm = useCallback((form) => {
    const errors = {};
    SWING_FIELDS.forEach((field) => {
      const raw = form[field];
      const parsed = Number(raw);
      if (raw === '' || !Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
        errors[field] = 'Must be a positive integer.';
      }
    });
    return errors;
  }, []);

  const handleWeeklyCycleSweepInputChange = (field) => (event) => {
    const { value } = event.target;
    setWeeklyCycleSweepForm((prev) => ({ ...prev, [field]: value }));
    setWeeklyCycleSweepFieldErrors((prevErrors) => {
      if (!prevErrors[field]) {
        return prevErrors;
      }
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      return nextErrors;
    });
    setIndicatorError('');
  };

  const validateWeeklyCycleSweepForm = useCallback((form) => {
    const errors = {};
    WEEKLY_CYCLE_SWEEP_FIELDS.forEach((field) => {
      const raw = form[field];
      const parsed = Number(raw);
      if (raw === '' || !Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
        errors[field] = 'Must be a positive integer.';
      }
    });
    return errors;
  }, []);

  const handleSaveSwing = async () => {
    const errors = validateSwingForm(swingForm);
    if (Object.keys(errors).length > 0) {
      setSwingFieldErrors(errors);
      setIndicatorError('Please fix the highlighted swing lookback fields before saving.');
      return;
    }

    setSwingSaving(true);
    setIndicatorError('');

    try {
      await Promise.all(
        SWING_FIELDS.map((field) =>
          updateSystemSetting(INDICATOR_SETTING_KEYS.swing[field], Number(swingForm[field]))
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Swing indicator settings updated successfully.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update swing indicator settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setSwingSaving(false);
    }
  };

  const handleResetSwing = async () => {
    setSwingSaving(true);
    setIndicatorError('');
    setSwingFieldErrors({});

    try {
      await Promise.all(
        SWING_FIELDS.map((field) =>
          deleteSystemSetting(INDICATOR_SETTING_KEYS.swing[field]).catch((error) => {
            if (error?.response?.status === 404) {
              return null;
            }
            throw error;
          })
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Swing indicator settings reset to defaults.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to reset swing indicator settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setSwingSaving(false);
    }
  };

  const handleSaveWeeklyCycleSweep = async () => {
    const errors = validateWeeklyCycleSweepForm(weeklyCycleSweepForm);
    if (Object.keys(errors).length > 0) {
      setWeeklyCycleSweepFieldErrors(errors);
      setIndicatorError('Please fix the highlighted cycle sweep fields before saving.');
      return;
    }

    setWeeklyCycleSweepSaving(true);
    setIndicatorError('');

    try {
      await Promise.all(
        WEEKLY_CYCLE_SWEEP_FIELDS.map((field) =>
          updateSystemSetting(
            INDICATOR_SETTING_KEYS.weeklyCycleSweep[field],
            Number(weeklyCycleSweepForm[field]),
          )
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Weekly cycle sweep settings updated successfully.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update weekly cycle sweep settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setWeeklyCycleSweepSaving(false);
    }
  };

  const handleResetWeeklyCycleSweep = async () => {
    setWeeklyCycleSweepSaving(true);
    setIndicatorError('');
    setWeeklyCycleSweepFieldErrors({});

    try {
      await Promise.all(
        WEEKLY_CYCLE_SWEEP_FIELDS.map((field) =>
          deleteSystemSetting(INDICATOR_SETTING_KEYS.weeklyCycleSweep[field]).catch((error) => {
            if (error?.response?.status === 404) {
              return null;
            }
            throw error;
          })
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Weekly cycle sweep settings reset to defaults.',
      });
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to reset weekly cycle sweep settings.';
      setIndicatorError(message);
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setWeeklyCycleSweepSaving(false);
    }
  };

  const handleRevertSwingChanges = () => {
    setSwingForm({ ...swingInitialForm });
    setSwingFieldErrors({});
    setIndicatorError('');
  };

  const handleRevertWeeklyCycleSweepChanges = () => {
    setWeeklyCycleSweepForm({ ...weeklyCycleSweepInitialForm });
    setWeeklyCycleSweepFieldErrors({});
    setIndicatorError('');
  };

  const handleToggleSymbol = async (symbolRecord) => {
    if (!symbolRecord) {
      return;
    }

    const nextState = !symbolRecord.isActive;
    setUpdatingSymbols((prev) => ({ ...prev, [symbolRecord.symbol]: true }));

    try {
      const response = await updateAdminSymbol(symbolRecord.symbol, { isActive: nextState });
      const normalizedResponse =
        response && response.symbol ? normalizeAdminSymbolRecord(response) : null;

      setSymbols((prev) =>
        prev.map((item) =>
          item.symbol === symbolRecord.symbol
            ? normalizedResponse ?? { ...item, isActive: nextState }
            : item
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${symbolRecord.symbol} marked ${nextState ? 'active' : 'inactive'}.`,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to update ${symbolRecord.symbol}.`;
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setUpdatingSymbols((prev) => {
        const next = { ...prev };
        delete next[symbolRecord.symbol];
        return next;
      });
    }
  };

  const handleToggleTimeframe = async (symbolRecord, timeframeKey) => {
    if (!symbolRecord || !timeframeKey) {
      return;
    }

    const timeframeLabel = timeframeKey.toUpperCase();
    const currentValue = Boolean(symbolRecord.supportedTimeframes?.[timeframeKey]);
    const mergedTimeframes = normalizeSupportedTimeframes({
      ...symbolRecord.supportedTimeframes,
      [timeframeKey]: !currentValue,
    });

    setUpdatingSymbols((prev) => ({ ...prev, [symbolRecord.symbol]: true }));

    try {
      const response = await updateAdminSymbol(symbolRecord.symbol, {
        supportedTimeframes: mergedTimeframes,
      });
      const normalizedResponse =
        response && response.symbol ? normalizeAdminSymbolRecord(response) : null;

      setSymbols((prev) =>
        prev.map((item) =>
          item.symbol === symbolRecord.symbol
            ? normalizedResponse ?? {
              ...item,
              supportedTimeframes: normalizeSupportedTimeframes(mergedTimeframes),
            }
            : item
        )
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${symbolRecord.symbol} ${!currentValue ? 'enabled' : 'disabled'} ${timeframeLabel} timeframe.`,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to update ${timeframeLabel} support for ${symbolRecord.symbol}.`;
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setUpdatingSymbols((prev) => {
        const next = { ...prev };
        delete next[symbolRecord.symbol];
        return next;
      });
    }
  };

  const resetNewSymbolForm = () => {
    setNewSymbolForm(createDefaultNewSymbolForm());
  };

  const handleNewSymbolFieldChange = (field, value) => {
    setNewSymbolForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewSymbolTimeframeChange = (timeframeKey, value) => {
    setNewSymbolForm((prev) => ({
      ...prev,
      supportedTimeframes: {
        ...prev.supportedTimeframes,
        [timeframeKey]: value,
      },
    }));
  };

  const handleCreateSymbol = async () => {
    if (addingSymbol) {
      return;
    }

    const trimmedSymbol = newSymbolForm.symbol.trim().toUpperCase();
    const trimmedName = newSymbolForm.name.trim();

    if (!trimmedSymbol) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Symbol is required to add a new entry.',
      });
      return;
    }

    try {
      setAddingSymbol(true);
      await createAdminSymbol({
        symbol: trimmedSymbol,
        name: trimmedName || trimmedSymbol,
        isActive: newSymbolForm.isActive,
        supportedTimeframes: newSymbolForm.supportedTimeframes,
      });
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${trimmedSymbol} added successfully.`,
      });
      resetNewSymbolForm();
      await loadSymbols();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to add ${trimmedSymbol || 'symbol'}.`;
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setAddingSymbol(false);
    }
  };

  const handleDeleteTrendlines = async () => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const trimmedSymbol = symbol.trim();
      const result = await deleteDailyTrendlines({ symbol: trimmedSymbol || undefined });

      setSnackbar({
        open: true,
        severity: 'success',
        message: result?.message || 'Trendlines deleted successfully.',
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to delete trendlines.';

      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerTrendlineDetection = async () => {
    if (detectLoading) {
      return;
    }

    try {
      setDetectLoading(true);
      const result = await triggerTrendlineDetection();
      setSnackbar({
        open: true,
        severity: 'success',
        message: result?.message || 'Trendline detection triggered.',
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to trigger trendline detection.';

      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setDetectLoading(false);
    }
  };

  const handleDeleteWeeklyTrendlines = async () => {
    if (weeklyLoading) {
      return;
    }

    try {
      setWeeklyLoading(true);
      const trimmedSymbol = weeklySymbol.trim();
      const result = await deleteWeeklyTrendlines({ symbol: trimmedSymbol || undefined });

      setSnackbar({
        open: true,
        severity: 'success',
        message: result?.message || 'Weekly trendlines deleted successfully.',
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to delete weekly trendlines.';

      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setWeeklyLoading(false);
    }
  };

  const handleTriggerWeeklyTrendlineDetection = async () => {
    if (weeklyDetectLoading) {
      return;
    }

    try {
      setWeeklyDetectLoading(true);
      const result = await triggerWeeklyTrendlineDetection();

      setSnackbar({
        open: true,
        severity: 'success',
        message: result?.message || 'Weekly trendline detection triggered.',
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to trigger weekly trendline detection.';

      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setWeeklyDetectLoading(false);
    }
  };

  const resolveBackfillInputs = () => {
    const trimmedSymbol = backfillSymbol.trim();
    if (!trimmedSymbol) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Symbol is required for backfill operations.',
      });
      return null;
    }

    const parsedYears = Number(backfillYears);
    const years = Number.isFinite(parsedYears) && parsedYears > 0 ? parsedYears : 3;

    return { symbol: trimmedSymbol.toUpperCase(), years };
  };

  const resolveHourlyInputs = () => {
    const base = resolveBackfillInputs();
    if (!base) { return null; }

    const parsedHours = Number(backfillHours);
    const hours =
      Number.isFinite(parsedHours) && parsedHours > 0
        ? Math.min(parsedHours, 2000)
        : 500;

    return { symbol: base.symbol, hours };
  };

  const parseSymbolsInput = (input) =>
    input
      .split(',')
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);

  const parseEventSymbolPayload = () => {
    const tokens = parseSymbolsInput(eventSymbolsInput);

    if (tokens.length === 0) {
      return {};
    }

    if (tokens.length === 1) {
      return { symbol: tokens[0] };
    }

    return { symbols: tokens };
  };

  const parseCustomEventSymbolPayload = () => {
    const tokens = parseSymbolsInput(customEventSymbolsInput);

    if (tokens.length === 0) {
      return {};
    }

    if (tokens.length === 1) {
      return { symbol: tokens[0] };
    }

    return { symbols: tokens };
  };

  const parseCustomEventClearSymbolPayload = () => {
    const tokens = parseSymbolsInput(customEventClearSymbolsInput);

    if (tokens.length === 0) {
      return {};
    }

    if (tokens.length === 1) {
      return { symbol: tokens[0] };
    }

    return { symbols: tokens };
  };

  const resolveIndicatorYears = () => {
    const parsed = Number(indicatorEventsYears);
    const bounded = Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 10) : 3;
    return bounded;
  };

  const describeEventTarget = (payload = {}) => {
    if (Array.isArray(payload.symbols) && payload.symbols.length) {
      return payload.symbols.join(', ');
    }
    if (payload.symbol) {
      return payload.symbol;
    }
    return 'all symbols';
  };

  const resolveBackfillEventSymbols = async (symbolPayload) => {
    if (Array.isArray(symbolPayload.symbols) && symbolPayload.symbols.length) {
      return symbolPayload.symbols;
    }
    if (symbolPayload.symbol) {
      return [symbolPayload.symbol];
    }

    // No symbols specified: fetch active symbols
    try {
      const activeSymbols = (symbols.length ? symbols : transformSymbols((await getAdminSymbols())?.symbols || []))
        .filter((item) => item.isActive)
        .map((item) => item.symbol);
      return activeSymbols;
    } catch {
      return [];
    }
  };

  const setBackfillLoadingState = (key, value) => {
    setBackfillLoading((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const performBackfill = async ({ key, fn, payload, defaultSuccess, defaultFailure }) => {
    if (backfillLoading[key]) {
      return;
    }

    try {
      setBackfillLoadingState(key, true);
      const result = await fn(payload);
      setSnackbar({
        open: true,
        severity: 'success',
        message: result?.message || defaultSuccess,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        defaultFailure;

      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setBackfillLoadingState(key, false);
    }
  };

  const handleBackfillDailyCandles = async () => {
    const inputs = resolveBackfillInputs();
    if (!inputs) { return; }

    await performBackfill({
      key: 'dailyCandles',
      fn: backfillDailyCandles,
      payload: inputs,
      defaultSuccess: `Daily candle backfill requested for ${inputs.symbol}`,
      defaultFailure: 'Failed to trigger daily candle backfill.',
    });
  };

  const handleBackfillWeeklyCandles = async () => {
    const inputs = resolveBackfillInputs();
    if (!inputs) { return; }

    await performBackfill({
      key: 'weeklyCandles',
      fn: backfillWeeklyCandles,
      payload: inputs,
      defaultSuccess: `Weekly candle backfill requested for ${inputs.symbol}`,
      defaultFailure: 'Failed to trigger weekly candle backfill.',
    });
  };

  const handleBackfillHourlyData = async () => {
    const inputs = resolveHourlyInputs();
    if (!inputs) { return; }

    await performBackfill({
      key: 'hourly',
      fn: backfillHourlyData,
      payload: inputs,
      defaultSuccess: `Hourly backfill requested for ${inputs.symbol} (${inputs.hours} candles)`,
      defaultFailure: 'Failed to trigger hourly backfill.',
    });
  };

  const handleBackfillSwings = async (timeframes, key) => {
    const inputs = resolveBackfillInputs();
    if (!inputs) { return; }

    await performBackfill({
      key,
      fn: backfillSwings,
      payload: { ...inputs, timeframes },
      defaultSuccess: `Swing/trendline backfill requested for ${inputs.symbol} (${timeframes.join(', ')})`,
      defaultFailure: 'Failed to trigger swing/trendline backfill.',
    });
  };

  const handleBackfillIndicatorEvents = async () => {
    if (backfillLoading.indicatorEvents) {
      return;
    }

    const years = resolveIndicatorYears();
    const symbolPayload = parseEventSymbolPayload();

    try {
      setBackfillLoadingState('indicatorEvents', true);

      const targetSymbols = await resolveBackfillEventSymbols(symbolPayload);
      if (!targetSymbols.length) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'No active symbols available for indicator event backfill.',
        });
        return;
      }

      const maxPerBatch = Math.max(1, Math.floor(INDICATOR_BACKFILL_YEARS_BUDGET / years));
      const batches = [];
      for (let i = 0; i < targetSymbols.length; i += maxPerBatch) {
        batches.push(targetSymbols.slice(i, i + maxPerBatch));
      }

      for (const batch of batches) {
        const payload = batch.length === 1 ? { years, symbol: batch[0] } : { years, symbols: batch };
        await backfillIndicatorEvents(payload);
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Indicator events backfill requested for ${targetSymbols.length} symbol(s) in ${batches.length} batch(es) (up to ${years} years each).`,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to trigger indicator events backfill.';
      setSnackbar({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setBackfillLoadingState('indicatorEvents', false);
    }
  };

  const handleRunCustomEventEvaluation = async () => {
    const symbolPayload = parseCustomEventSymbolPayload();
    const accountId = user?.accountId || user?.id || user?.account?.id || null;
    const accountScopeEnabled = Boolean(customEventAccountOnly);
    const lookbackDays = customEventLookbackDays.trim() ? Number(customEventLookbackDays) : null;

    if (accountScopeEnabled && !accountId) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Unable to determine the current account ID for scoping.',
      });
      return;
    }

    if (customEventLookbackDays.trim() && (!Number.isFinite(lookbackDays) || lookbackDays <= 0)) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Lookback days must be a positive number.',
      });
      return;
    }

    await performBackfill({
      key: 'customEvents',
      fn: runCustomEventEvaluation,
      payload: {
        ...symbolPayload,
        ...(accountScopeEnabled ? { accountId } : {}),
        ...(lookbackDays ? { lookbackDays } : {}),
      },
      defaultSuccess: `Custom event evaluation requested for ${describeEventTarget(symbolPayload)}${accountScopeEnabled ? ' (current account)' : ''}${lookbackDays ? ` • ${lookbackDays}d lookback` : ''}.`,
      defaultFailure: 'Failed to trigger custom event evaluation.',
    });
  };

  const handleClearCustomEventOccurrences = async () => {
    const symbolPayload = parseCustomEventClearSymbolPayload();
    const accountId = user?.accountId || user?.id || user?.account?.id || null;
    const accountScopeEnabled = Boolean(customEventClearAccountOnly);

    if (accountScopeEnabled && !accountId) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Unable to determine the current account ID for scoping.',
      });
      return;
    }

    await performBackfill({
      key: 'customEventClear',
      fn: clearCustomEvents,
      payload: {
        ...symbolPayload,
        ...(accountScopeEnabled ? { accountId } : {}),
      },
      defaultSuccess: `Custom event occurrences deletion requested for ${describeEventTarget(symbolPayload)}${accountScopeEnabled ? ' (current account)' : ''}.`,
      defaultFailure: 'Failed to trigger custom event deletion.',
    });
  };

  const handleClearEvents = async () => {
    const symbolPayload = parseEventSymbolPayload();
    const targetDescription = describeEventTarget(symbolPayload);

    await performBackfill({
      key: 'clearEvents',
      fn: clearEvents,
      payload: symbolPayload,
      defaultSuccess: `Event deletion requested for ${targetDescription}`,
      defaultFailure: 'Failed to trigger event deletion.',
    });
  };

  if (permissionsLoading || !permissionsLoaded) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography 
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.75rem', md: '2rem' },
            fontWeight: 600,
            letterSpacing: '-0.03em',
            color: 'var(--dwlf-text)',
            mb: 2,
          }}
        >
          Admin Tools
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DWLFLoader size="tiny" showText={false} />
          <Typography 
            sx={{ 
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-50)',
            }}
          >
            Checking credentials...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!canManageRoles && !canManageQuotas) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography 
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.75rem', md: '2rem' },
            fontWeight: 600,
            letterSpacing: '-0.03em',
            color: 'var(--dwlf-text)',
            mb: 2,
          }}
        >
          Admin Tools
        </Typography>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.75rem', md: '2rem' },
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'var(--dwlf-text)',
              mb: 1,
            }}
          >
            Admin Tools
          </Typography>
          <Typography 
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-50)',
              mb: 2,
            }}
          >
            Manage privileged operations that impact global application data
          </Typography>
          <Tabs
            value={validTab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ 
              '& .MuiTab-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              },
            }}
          >
            {userIsAdmin && <Tab label="Analytics" value="analytics" />}
            {userIsAdmin && <Tab label="Symbol Management" value="symbols" />}
            {userIsAdmin && <Tab label="Data Management" value="trendlines" />}
            {userIsAdmin && <Tab label="Indicator Configuration" value="indicators" />}
            {userIsAdmin && <Tab label="System Settings" value="settings" />}
            {userIsAdmin && <Tab label="Disclaimer Management" value="disclaimer" />}
            {userIsAdmin && <Tab label="Users" value="users" />}
            {canManageRoles && <Tab label="Roles" value="roles" />}
            {canManageRoles && <Tab label="User Roles" value="userRoles" />}
            {canManageQuotas && <Tab label="Quotas" value="quotas" />}
          </Tabs>
        </Box>

        {validTab === 'symbols' && userIsAdmin && (
          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
            }}
          >
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Symbol Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review tracked symbols, toggle their active status, and register new entries for data pulls.
                  </Typography>
                </Box>
                <Tooltip title="Refresh symbols">
                  <span>
                    <IconButton onClick={handleRefreshSymbols} disabled={symbolsLoading}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {symbolsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <DWLFLoader size="small" />
                </Box>
              ) : symbolsError ? (
                <Alert severity="error">{symbolsError}</Alert>
              ) : symbols.length === 0 ? (
                <Alert severity="info">No symbols found.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Tags</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">4H</TableCell>
                        <TableCell align="center">1H</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {symbols.map((item) => (
                        <TableRow key={item.symbol} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.symbol}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.name || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
                              {(symbolTags[item.symbol] || []).map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  onDelete={async () => {
                                    try {
                                      await removeSymbolTag(item.symbol, tag);
                                      setSymbolTags((prev) => ({
                                        ...prev,
                                        [item.symbol]: (prev[item.symbol] || []).filter((t) => t !== tag),
                                      }));
                                    } catch (e) {
                                      /* ignore */
                                    }
                                  }}
                                />
                              ))}
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={allTags}
                                sx={{ minWidth: 120 }}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Add tag" variant="standard" size="small" />
                                )}
                                onChange={async (e, value) => {
                                  if (!value) {return;}
                                  const normalizedValue = value.toLowerCase();
                                  try {
                                    await addSymbolTag(item.symbol, item.name, normalizedValue);
                                    setSymbolTags((prev) => ({
                                      ...prev,
                                      [item.symbol]: [...new Set([...(prev[item.symbol] || []), normalizedValue])],
                                    }));
                                    setAllTags((prev) =>
                                      prev.includes(normalizedValue) ? prev : [...prev, normalizedValue]
                                    );
                                  } catch (err) {
                                    /* ignore */
                                  }
                                }}
                                value={null}
                                blurOnSelect
                                clearOnBlur
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={item.isActive ? 'success' : 'default'}
                              variant={item.isActive ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                item.supportedTimeframes?.['4h']
                                  ? 'Disable 4H timeframe'
                                  : 'Enable 4H timeframe'
                              }
                            >
                              <span>
                                <Switch
                                  checked={Boolean(item.supportedTimeframes?.['4h'])}
                                  onChange={() => handleToggleTimeframe(item, '4h')}
                                  disabled={Boolean(updatingSymbols[item.symbol])}
                                  inputProps={{ 'aria-label': `Toggle 4H timeframe for ${item.symbol}` }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                item.supportedTimeframes?.['1h']
                                  ? 'Disable 1H timeframe'
                                  : 'Enable 1H timeframe'
                              }
                            >
                              <span>
                                <Switch
                                  checked={Boolean(item.supportedTimeframes?.['1h'])}
                                  onChange={() => handleToggleTimeframe(item, '1h')}
                                  disabled={Boolean(updatingSymbols[item.symbol])}
                                  inputProps={{ 'aria-label': `Toggle 1H timeframe for ${item.symbol}` }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                              {updatingSymbols[item.symbol] && <DWLFLoader size="tiny" showText={false} />}
                              <Tooltip title={item.isActive ? 'Deactivate symbol' : 'Activate symbol'}>
                                <span>
                                  <Switch
                                    checked={item.isActive}
                                    onChange={() => handleToggleSymbol(item)}
                                    disabled={Boolean(updatingSymbols[item.symbol])}
                                    inputProps={{ 'aria-label': `Toggle ${item.symbol}` }}
                                  />
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
            <Divider sx={{ borderColor: 'var(--dwlf-ink-10)' }} />
            <CardActions
              sx={{
                px: 3,
                pb: 3,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ width: '100%', alignItems: { xs: 'stretch', md: 'center' } }}
              >
                <TextField
                  label="Symbol"
                  placeholder="e.g. NVDA or BTC-USD"
                  value={newSymbolForm.symbol}
                  onChange={(event) => handleNewSymbolFieldChange('symbol', event.target.value)}
                  sx={{ flex: { md: 1 } }}
                />
                <TextField
                  label="Name (optional)"
                  placeholder="Friendly name"
                  value={newSymbolForm.name}
                  onChange={(event) => handleNewSymbolFieldChange('name', event.target.value)}
                  sx={{ flex: { md: 1 } }}
                />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems="center"
                  sx={{ flexShrink: 0 }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newSymbolForm.isActive}
                        onChange={(event) => handleNewSymbolFieldChange('isActive', event.target.checked)}
                        inputProps={{ 'aria-label': 'New symbol active state' }}
                      />
                    }
                    label={newSymbolForm.isActive ? 'Active' : 'Inactive'}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(newSymbolForm.supportedTimeframes?.['4h'])}
                        onChange={(event) => handleNewSymbolTimeframeChange('4h', event.target.checked)}
                        inputProps={{ 'aria-label': 'Support 4H timeframe' }}
                      />
                    }
                    label="4H"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(newSymbolForm.supportedTimeframes?.['1h'])}
                        onChange={(event) => handleNewSymbolTimeframeChange('1h', event.target.checked)}
                        inputProps={{ 'aria-label': 'Support 1H timeframe' }}
                      />
                    }
                    label="1H"
                  />
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleCreateSymbol}
                    disabled={addingSymbol}
                  >
                    {addingSymbol ? 'Adding…' : 'Add Symbol'}
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={resetNewSymbolForm}
                    disabled={addingSymbol}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </CardActions>
          </Card>
        )}

        {validTab === 'trendlines' && userIsAdmin && (
          <>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Delete Daily Trendlines
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Permanently remove generated daily trendlines from the data store. You can target a
                  single symbol or leave the field blank to purge every symbol. This operation cannot be
                  undone.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Symbol (optional)"
                    placeholder="e.g. NVDA, BTC-USD"
                    value={symbol}
                    onChange={(event) => setSymbol(event.target.value)}
                    sx={{ flex: 1 }}
                    helperText="Specify to delete only this symbol's trendlines. Leave empty to delete all."
                  />
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteTrendlines}
                  disabled={loading}
                >
                  {loading ? 'Deleting…' : 'Delete Trendlines'}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => setSymbol('')}
                  disabled={loading || !symbol}
                >
                  Clear
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Trigger Daily Trendline Detection
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Kick off the scheduled job immediately to recompute all daily trendlines. This runs the
                  same Lambda invoked by the daily schedule, allowing you to refresh data on demand.
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This job can take several minutes and consumes AWS resources. Avoid running it repeatedly.
                </Alert>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTriggerTrendlineDetection}
                  disabled={detectLoading}
                >
                  {detectLoading ? 'Triggering…' : 'Run Detection Job'}
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Delete Weekly Trendlines
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Remove generated weekly trendlines. Target a specific symbol or leave blank to purge every
                  symbol&apos;s weekly lines. This cannot be undone.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Symbol (optional)"
                    placeholder="e.g. NVDA, BTC-USD"
                    value={weeklySymbol}
                    onChange={(event) => setWeeklySymbol(event.target.value)}
                    sx={{ flex: 1 }}
                    helperText="Specify to delete only this symbol's weekly trendlines. Leave empty to delete all."
                  />
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteWeeklyTrendlines}
                  disabled={weeklyLoading}
                >
                  {weeklyLoading ? 'Deleting…' : 'Delete Weekly Trendlines'}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => setWeeklySymbol('')}
                  disabled={weeklyLoading || !weeklySymbol}
                >
                  Clear
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Trigger Weekly Trendline Detection
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Run the weekly trendline job now using the same Lambda that operates on the overnight
                  schedule. Useful after purging or backfilling data.
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This job gathers data and recalculates weekly lines. Allow it time to finish before running again.
                </Alert>
              </CardContent>
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTriggerWeeklyTrendlineDetection}
                  disabled={weeklyDetectLoading}
                >
                  {weeklyDetectLoading ? 'Triggering…' : 'Run Weekly Detection'}
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backfill Market Data & Swings
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Use these tools when onboarding a new symbol or repairing stale datasets. Jobs run
                  asynchronously via the scheduled-jobs Lambdas, so results may take a few minutes to appear.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Symbol"
                    placeholder="e.g. TSLA"
                    value={backfillSymbol}
                    onChange={(event) => setBackfillSymbol(event.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Years (default 3)"
                    type="number"
                    value={backfillYears}
                    onChange={(event) => setBackfillYears(event.target.value)}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ width: { xs: '100%', sm: 180 } }}
                  />
                  <TextField
                    label="Hours (default 500)"
                    type="number"
                    value={backfillHours}
                    onChange={(event) => setBackfillHours(event.target.value)}
                    inputProps={{ min: 1, max: 2000 }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </Stack>
                <Alert severity="info">
                  Candle backfills refresh price history; swing jobs recompute swing points and trendlines for
                  the selected timeframes.
                </Alert>
              </CardContent>
              <CardActions
                sx={{
                  px: 3,
                  pb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBackfillDailyCandles}
                  disabled={backfillLoading.dailyCandles}
                >
                  {backfillLoading.dailyCandles ? 'Triggering…' : 'Backfill Daily Candles'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBackfillWeeklyCandles}
                  disabled={backfillLoading.weeklyCandles}
                >
                  {backfillLoading.weeklyCandles ? 'Triggering…' : 'Backfill Weekly Candles'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBackfillHourlyData}
                  disabled={backfillLoading.hourly}
                >
                  {backfillLoading.hourly ? 'Triggering…' : 'Backfill Hourly Data'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleBackfillSwings(['daily'], 'swingsDaily')}
                  disabled={backfillLoading.swingsDaily}
                >
                  {backfillLoading.swingsDaily ? 'Triggering…' : 'Backfill Daily Swings'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleBackfillSwings(['weekly'], 'swingsWeekly')}
                  disabled={backfillLoading.swingsWeekly}
                >
                  {backfillLoading.swingsWeekly ? 'Triggering…' : 'Backfill Weekly Swings'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleBackfillSwings(['daily', 'weekly'], 'swingsBoth')}
                  disabled={backfillLoading.swingsBoth}
                >
                  {backfillLoading.swingsBoth ? 'Triggering…' : 'Backfill Daily & Weekly Swings'}
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Indicator Event Maintenance
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Re-run indicator event detection for the selected lookback window or purge stored events. Leave
                  the symbol list blank to target all symbols, or supply a comma-separated list to scope the job.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Symbols (optional)"
                    placeholder="e.g. TSLA, NVDA"
                    value={eventSymbolsInput}
                    onChange={(event) => setEventSymbolsInput(event.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Years for event backfill"
                    type="number"
                    value={indicatorEventsYears}
                    onChange={(event) => setIndicatorEventsYears(event.target.value)}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </Stack>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Indicator backfills use the dwlf-indicators library to regenerate events per symbol/timeframe.
                  Clearing events is destructive and should be reserved for recovery scenarios.
                </Alert>
              </CardContent>
              <CardActions
                sx={{
                  px: 3,
                  pb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBackfillIndicatorEvents}
                  disabled={backfillLoading.indicatorEvents}
                >
                  {backfillLoading.indicatorEvents ? 'Triggering…' : 'Backfill Indicator Events'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearEvents}
                  disabled={backfillLoading.clearEvents}
                >
                  {backfillLoading.clearEvents ? 'Triggering…' : 'Clear Events'}
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Custom Event Evaluation
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Trigger the custom event job manually. Leave symbols blank to run against all active custom event
                  symbol associations.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Symbols (optional)"
                    placeholder="e.g. TSLA, NVDA"
                    value={customEventSymbolsInput}
                    onChange={(event) => setCustomEventSymbolsInput(event.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Lookback (days)"
                    placeholder="e.g. 730"
                    type="number"
                    value={customEventLookbackDays}
                    onChange={(event) => setCustomEventLookbackDays(event.target.value)}
                    inputProps={{ min: 1, max: 4000 }}
                    sx={{ width: { xs: '100%', sm: 180 } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={customEventAccountOnly}
                        onChange={(event) => setCustomEventAccountOnly(event.target.checked)}
                      />
                    }
                    label="Limit to my account"
                  />
                </Stack>
                <Alert severity="info">
                  The job evaluates compiled custom events using dwlf-indicators. Add a lookback window to backfill
                  older occurrences. Limiting to your account will scope evaluation to your event-symbol associations only.
                </Alert>
              </CardContent>
              <CardActions
                sx={{
                  px: 3,
                  pb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRunCustomEventEvaluation}
                  disabled={backfillLoading.customEvents}
                >
                  {backfillLoading.customEvents ? 'Triggering…' : 'Run Custom Event Job'}
                </Button>
              </CardActions>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Clear Custom Event Occurrences
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Delete stored custom event occurrences. Leave symbols blank to target all custom event symbols.
                  This does not affect the custom event definitions.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Symbols (optional)"
                    placeholder="e.g. TSLA, NVDA"
                    value={customEventClearSymbolsInput}
                    onChange={(event) => setCustomEventClearSymbolsInput(event.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={customEventClearAccountOnly}
                        onChange={(event) => setCustomEventClearAccountOnly(event.target.checked)}
                      />
                    }
                    label="Limit to my account"
                  />
                </Stack>
                <Alert severity="warning">
                  This permanently deletes custom event occurrences from the events table.
                </Alert>
              </CardContent>
              <CardActions
                sx={{
                  px: 3,
                  pb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearCustomEventOccurrences}
                  disabled={backfillLoading.customEventClear}
                >
                  {backfillLoading.customEventClear ? 'Triggering…' : 'Clear Custom Event Occurrences'}
                </Button>
              </CardActions>
            </Card>
          </>
        )}

        {validTab === 'indicators' && userIsAdmin && (
          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Indicator Configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage global indicator parameters used by back-end calculations and analytics.
                  </Typography>
                </Box>
                <Tooltip title="Refresh indicator settings">
                  <span>
                    <IconButton onClick={handleRefreshSettings} disabled={settingsLoading || indicatorBusy}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <DWLFLoader size="small" />
                </Box>
              ) : settingsError ? (
                <Alert severity="error">{settingsError}</Alert>
              ) : (
                <Stack spacing={3}>
                  {indicatorError && <Alert severity="error">{indicatorError}</Alert>}
                  <Stack spacing={2}>
                    <Alert severity="info">
                      The Double Smoothed Stochastic (DSS) indicator applies two EMA smoothing steps to a stochastic oscillator
                      and uses a third EMA for the signal line. Adjust these periods to tune responsiveness versus stability across the platform.
                    </Alert>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        DSS Indicator
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These values feed scheduled indicator calculations and any realtime analytics that rely on the DSS output.
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(4, minmax(0, 1fr))',
                          },
                        }}
                      >
                        {DSS_FIELD_CONFIG.map(({ field, label, helperText }) => (
                          <TextField
                            key={field}
                            label={label}
                            type="number"
                            value={dssForm[field]}
                            onChange={handleDssInputChange(field)}
                            disabled={indicatorBusy || settingsLoading}
                            inputProps={{ min: 1 }}
                            error={Boolean(dssFieldErrors[field])}
                            helperText={dssFieldErrors[field] || helperText}
                            fullWidth
                          />
                        ))}
                      </Box>
                    </Box>
                    <Stack spacing={0.75} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                        DSS Actions
                      </Typography>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
                        <Button
                          variant="text"
                          color="inherit"
                          onClick={handleRevertDssChanges}
                          disabled={!dssDirty || indicatorBusy || settingsLoading}
                        >
                          Revert Changes
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleResetDss}
                          disabled={indicatorBusy || settingsLoading}
                        >
                          Reset to Defaults
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveDss}
                          disabled={!dssDirty || indicatorBusy || settingsLoading}
                        >
                          {dssSaving ? 'Saving…' : 'Save Changes'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Divider />
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Bollinger Bands plot an envelope around price using a moving-average basis and standard deviation multiplier.
                      Configure these parameters to align platform analytics with your preferred volatility assumptions.
                    </Alert>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Bollinger Bands
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Updates here drive backend calculations for the upper, middle, and lower band series published across DWLF tools.
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(4, minmax(0, 1fr))',
                          },
                        }}
                      >
                        {BOLLINGER_FIELD_CONFIG.map(({ field, label, helperText, type, options = [], inputProps }) => (
                          <TextField
                            key={field}
                            label={label}
                            value={bollingerForm[field]}
                            onChange={handleBollingerInputChange(field)}
                            disabled={indicatorBusy || settingsLoading}
                            type={type === 'number' ? 'number' : undefined}
                            select={type === 'select'}
                            inputProps={type === 'number' ? inputProps : undefined}
                            error={Boolean(bollingerFieldErrors[field])}
                            helperText={bollingerFieldErrors[field] || helperText}
                            fullWidth
                          >
                            {type === 'select' &&
                              options.map(({ value, label: optionLabel }) => (
                                <MenuItem key={value} value={value}>
                                  {optionLabel}
                                </MenuItem>
                              ))}
                          </TextField>
                        ))}
                      </Box>
                      <Stack spacing={0.75} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                          Bollinger Actions
                        </Typography>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
                          <Button
                            variant="text"
                            color="inherit"
                            onClick={handleRevertBollingerChanges}
                            disabled={!bollingerDirty || indicatorBusy || settingsLoading}
                          >
                            Revert Changes
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleResetBollinger}
                            disabled={indicatorBusy || settingsLoading}
                          >
                            Reset to Defaults
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveBollinger}
                            disabled={!bollingerDirty || indicatorBusy || settingsLoading}
                          >
                            {bollingerSaving ? 'Saving…' : 'Save Changes'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                  <Divider />
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Configure EMA Cloud defaults used for alignment and cloud-hit detection across DWLF tools.
                      Lengths accept a comma-separated list (minimum two), and sustained candles control how long an alignment must persist before firing sustained events.
                    </Alert>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        EMA Cloud
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These settings define the default EMA lengths in the cloud and the confirmation window for sustained alignment alerts.
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                          },
                        }}
                      >
                        {EMA_CLOUD_FIELD_CONFIG.map(({ field, label, helperText, type, inputProps }) => (
                          <TextField
                            key={field}
                            label={label}
                            value={emaCloudForm[field]}
                            onChange={handleEmaCloudInputChange(field)}
                            disabled={indicatorBusy || settingsLoading}
                            type={type === 'number' ? 'number' : undefined}
                            inputProps={type === 'number' ? inputProps : undefined}
                            error={Boolean(emaCloudFieldErrors[field])}
                            helperText={emaCloudFieldErrors[field] || helperText}
                            fullWidth
                          />
                        ))}
                      </Box>
                      <Stack spacing={0.75} alignItems={{ xs: 'stretch', md: 'flex-end' }} sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                          EMA Cloud Actions
                        </Typography>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
                          <Button
                            variant="text"
                            color="inherit"
                            onClick={handleRevertEmaCloudChanges}
                            disabled={!emaCloudDirty || indicatorBusy || settingsLoading}
                          >
                            Revert Changes
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleResetEmaCloud}
                            disabled={indicatorBusy || settingsLoading}
                          >
                            Reset to Defaults
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveEmaCloud}
                            disabled={!emaCloudDirty || indicatorBusy || settingsLoading}
                          >
                            {emaCloudSaving ? 'Saving…' : 'Save Changes'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                  <Divider />
                  {['weekly', 'daily', 'hourly'].flatMap((tf) => (
                    ['low', 'high'].map((side) => (
                      <CycleSettingsSection
                        key={`${tf}-${side}`}
                        timeframe={tf}
                        side={side}
                        settings={settings}
                        symbols={symbols}
                        loadSettings={loadSettings}
                        setSnackbar={setSnackbar}
                        setIndicatorError={setIndicatorError}
                        indicatorBusy={indicatorBusy}
                        settingsLoading={settingsLoading}
                      />
                    ))
                  ))}
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Cycle sweep detection watches for quick reclaim moves after a confirmed weekly cycle pivot is swept.
                    </Alert>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Weekly Cycle Sweeps
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure how many weekly bars price has to reclaim after a sweep of the last confirmed cycle pivot.
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(3, minmax(0, 1fr))',
                          },
                        }}
                      >
                        {WEEKLY_CYCLE_SWEEP_FIELD_CONFIG.map(({ field, label, helperText, type, inputProps }) => (
                          <TextField
                            key={field}
                            label={label}
                            type={type === 'number' ? 'number' : undefined}
                            value={weeklyCycleSweepForm[field]}
                            onChange={handleWeeklyCycleSweepInputChange(field)}
                            disabled={indicatorBusy || settingsLoading}
                            inputProps={type === 'number' ? inputProps : undefined}
                            error={Boolean(weeklyCycleSweepFieldErrors[field])}
                            helperText={weeklyCycleSweepFieldErrors[field] || helperText}
                            fullWidth
                          />
                        ))}
                      </Box>
                      <Stack spacing={0.75} alignItems={{ xs: 'stretch', md: 'flex-end' }} sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                          Cycle Sweep Actions
                        </Typography>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
                          <Button
                            variant="text"
                            color="inherit"
                            onClick={handleRevertWeeklyCycleSweepChanges}
                            disabled={!weeklyCycleSweepDirty || indicatorBusy || settingsLoading}
                          >
                            Revert Changes
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleResetWeeklyCycleSweep}
                            disabled={indicatorBusy || settingsLoading}
                          >
                            Reset to Defaults
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveWeeklyCycleSweep}
                            disabled={!weeklyCycleSweepDirty || indicatorBusy || settingsLoading}
                          >
                            {weeklyCycleSweepSaving ? 'Saving…' : 'Save Changes'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                  <Divider />
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Swing detection parameters define the lookback window for identifying swing highs and lows.
                      Adjust these values to fine-tune the sensitivity of swing detection.
                    </Alert>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Swing Detection
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These settings control the number of candles on each side (high/low) required to confirm a swing point.
                        Adjusting these can help the system better identify market reversals.
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(3, minmax(0, 1fr))',
                          },
                        }}
                      >
                        {SWING_FIELD_CONFIG.map(({ field, label, helperText }) => (
                          <TextField
                            key={field}
                            label={label}
                            type="number"
                            value={swingForm[field]}
                            onChange={handleSwingInputChange(field)}
                            disabled={indicatorBusy || settingsLoading}
                            inputProps={{ min: 1, step: 1 }}
                            error={Boolean(swingFieldErrors[field])}
                            helperText={swingFieldErrors[field] || helperText}
                            fullWidth
                          />
                        ))}
                      </Box>
                      <Stack spacing={0.75} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                          Swing Actions
                        </Typography>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end">
                          <Button
                            variant="text"
                            color="inherit"
                            onClick={handleRevertSwingChanges}
                            disabled={!swingDirty || indicatorBusy || settingsLoading}
                          >
                            Revert Changes
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleResetSwing}
                            disabled={indicatorBusy || settingsLoading}
                          >
                            Reset to Defaults
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveSwing}
                            disabled={!swingDirty || indicatorBusy || settingsLoading}
                          >
                            {swingSaving ? 'Saving…' : 'Save Changes'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {validTab === 'settings' && userIsAdmin && (
          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
            }}
          >
            <CardContent>
              {/* Registration toggle — prominent control at the top */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: registrationEnabled === null
                    ? 'divider'
                    : registrationEnabled ? 'success.dark' : 'error.dark',
                  backgroundColor: registrationEnabled === null
                    ? 'transparent'
                    : registrationEnabled
                      ? 'rgba(46, 125, 50, 0.08)'
                      : 'rgba(211, 47, 47, 0.08)',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    User Registration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {registrationEnabled === null
                      ? 'Loading registration status…'
                      : registrationEnabled
                        ? 'New users can sign up via Google OAuth and agent registration'
                        : 'Registration is closed — only existing users can log in'}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!registrationEnabled}
                      onChange={handleToggleRegistration}
                      disabled={registrationToggling || registrationEnabled === null}
                      color="success"
                    />
                  }
                  label={registrationEnabled === null ? '…' : registrationEnabled ? 'Open' : 'Closed'}
                  labelPlacement="start"
                  sx={{ ml: 2 }}
                />
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    System Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure global overrides for backend services. These values apply immediately after saving.
                  </Typography>
                </Box>
                <Tooltip title="Refresh settings">
                  <span>
                    <IconButton onClick={handleRefreshSettings} disabled={settingsLoading}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <DWLFLoader size="small" />
                </Box>
              ) : settingsError ? (
                <Alert severity="error">{settingsError}</Alert>
              ) : settings.length === 0 ? (
                <Alert severity="info">No system settings found.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Setting</TableCell>
                        <TableCell>Effective Value</TableCell>
                        <TableCell width="40%">Override</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {settings
                        .filter((setting) => !setting.key.startsWith('indicators.'))
                        .map((setting) => {
                          const hasOptions = Array.isArray(setting.options) && setting.options.length > 0;
                          const multiline = !hasOptions && (setting.inputValue.includes('\n') || setting.inputValue.length > 40);
                          const description = SETTING_DESCRIPTIONS[setting.key];
                          return (
                            <TableRow key={setting.key} hover>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {setting.key}
                                    </Typography>
                                    {description && (
                                      <Tooltip title={description}>
                                        <span>
                                          <IconButton size="small">
                                            <InfoOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      label={setting.isDefault ? 'Default' : 'Override'}
                                      size="small"
                                      color={setting.isDefault ? 'default' : 'primary'}
                                      variant={setting.isDefault ? 'outlined' : 'filled'}
                                    />
                                    {setting.dirty && <Chip label="Unsaved" size="small" color="warning" />}
                                  </Stack>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {serializeSettingValue(setting.value) || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={setting.inputValue}
                                  onChange={(event) => handleSettingInputChange(setting.key, event.target.value)}
                                  size="small"
                                  fullWidth
                                  select={hasOptions}
                                  multiline={multiline}
                                  minRows={multiline ? 3 : undefined}
                                  maxRows={multiline ? 8 : undefined}
                                  placeholder={hasOptions ? undefined : 'Enter override value'}
                                  disabled={setting.saving}
                                >
                                  {hasOptions &&
                                  setting.options.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatUpdatedAt(setting.updatedAt)}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleSaveSetting(setting.key)}
                                    disabled={setting.saving || !setting.dirty}
                                  >
                                    {setting.saving ? 'Saving…' : 'Save'}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="inherit"
                                    onClick={() => handleResetSetting(setting.key)}
                                    disabled={setting.saving || setting.isDefault}
                                  >
                                  Reset
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {validTab === 'analytics' && userIsAdmin && <Analytics />}

        {validTab === 'disclaimer' && userIsAdmin && <DisclaimerAdmin />}

        {validTab === 'users' && userIsAdmin && (
          <Card
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
            }}
          >
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    User Accounts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and manage user accounts. Admins can activate accounts that are pending email verification.
                  </Typography>
                </Box>
                <Tooltip title="Refresh users">
                  <span>
                    <IconButton onClick={handleRefreshUsers} disabled={usersLoading}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {usersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <DWLFLoader size="small" />
                </Box>
              ) : usersError ? (
                <Alert severity="error">{usersError}</Alert>
              ) : users.length === 0 ? (
                <Alert severity="info">No accounts found.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Account ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.accountId || user.id} hover>
                          <TableCell>
                            <Typography variant="body2">{user.email || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {user.accountId || user.id || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                user.status === 'active' ? 'Active'
                                  : user.status === 'pending_activation' ? 'Pending'
                                    : user.status === 'suspended' ? 'Suspended'
                                      : user.status === 'disabled' ? 'Disabled'
                                        : user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
                                          : 'Unknown'
                              }
                              size="small"
                              color={
                                user.status === 'active' ? 'success'
                                  : user.status === 'pending_activation' ? 'warning'
                                    : user.status === 'suspended' || user.status === 'disabled' ? 'error'
                                      : 'default'
                              }
                              variant={user.status === 'active' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {user.status === 'pending_activation' && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleActivateAccount(user.accountId || user.id)}
                                disabled={!!activatingAccountId}
                              >
                                {activatingAccountId === (user.accountId || user.id) ? 'Activating...' : 'Activate'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {validTab === 'roles' && canManageRoles && <RoleManagement />}

        {validTab === 'userRoles' && canManageRoles && <UserRoleAssignment />}

        {validTab === 'quotas' && canManageQuotas && <QuotaManagement />}
      </Stack>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        autoHideDuration={6000}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPage;
